import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { withRouter, Link } from 'react-router-dom';
import {
  Menu, Popup, Loader, Header, Divider, Button, Icon,
} from 'semantic-ui-react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import actions from 'actions';
import styled from 'styled-components';
import api from 'api';
import util from 'utils/utils';
import websocketApi from 'api/websocket';
import { EmptyBox } from 'components/lib/UI';
import NotificationItem from './NotificationItems';
import CircularButtonWithNumber from '../CircularButtonWithNumber';

const StyledNotificationTray = styled.div`
  min-width: ${({ theme }) => (theme.sizes.isMobile ? '100%' : '500px;')};
  max-height: ${({ theme }) => (theme.sizes.isMobile ? null : '600px')} !important;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 9999;
`;
const StyledMenu = styled(Menu)`
  min-width: ${({ theme }) => (theme.sizes.isMobile ? '100%' : '500px')} !important;
  max-height: ${({ theme }) => (theme.sizes.isMobile ? null : '600px')} !important;
  overflow: auto;
`;

const mapStateToProps = (state) => {
  const { user, notifications } = state;
  return {
    user, notifications: notifications.notifications, page: notifications.page, limit: notifications.limit, loading: notifications.loading, unseenNotifications: notifications.notifications.filter(n => !n.seen).length, total: notifications.total, canExtend: notifications.canExtend, trayOpen: notifications.trayOpen, reload: notifications.reload,
  };
};

const mapDispatchToProps = dispatch => ({
  onNotificationsRequested: () => dispatch(actions.notifications.requestNotifications()),
  onNotificationsReceived: (notifications, total, canExtend) => dispatch(actions.notifications.receiveNotifications(notifications, total, canExtend)),
  markNotificationsAsSeen: notifications => dispatch(actions.notifications.markNotificationsAsSeen(notifications)),
  markNotificationAsInteracted: notification => dispatch(actions.notifications.markNotificationAsInteracted(notification)),
  requestExtendNotifications: () => dispatch(actions.notifications.requestExtendNotifications()),
  extendNotifications: (notifications, total, canExtend) => dispatch(actions.notifications.extendNotifications(notifications, total, canExtend)),
  setTrayOpen: open => dispatch(actions.notifications.setTrayOpen(open)),
  setReload: reload => dispatch(actions.notifications.reload(reload)),
});

const NotificationTray = withTranslation()(connect(mapStateToProps, mapDispatchToProps)(({
  isMobile, loading, notifications = [], t, total, user, canExtend, onNotificationsRequested, onNotificationsReceived, markNotificationAsInteracted, requestExtendNotifications, extendNotifications, markNotificationsAsSeen, page, limit, trayOpen: popupOpen, setTrayOpen: setPopupOpen, style, reload, setReload,
}) => {
  const [invitations, setInvitations] = useState([]);
  const userId = user?._id;
  const acceptableInvitations = useMemo(() => invitations.filter((invitation) => !invitation.createdByCurrentUser), [invitations]);

  const getInvitations = useCallback(() => {
    api.invitations.get(({ invitations: newInvitations }) => setInvitations(newInvitations), () => { });
  }, []);

  const onInvitationHandled = useCallback((invitationId) => {
    setInvitations(prev => prev.filter(i => i._id !== invitationId));
  }, []);

  const getNotifications = useCallback((nextPage) => {
    onNotificationsRequested();
    api.notifications.get(userId, nextPage, limit,
      ({ total: newTotal, notifications: newNotifications, canExtend: newCanExtend }) => {
        if (nextPage > 1) {
          extendNotifications(newNotifications, newTotal, newCanExtend);
        } else {
          onNotificationsReceived(newNotifications, newTotal, newCanExtend);
        }
      },
      () => onNotificationsReceived([]));
  }, [onNotificationsRequested, onNotificationsReceived, userId, extendNotifications, limit]);

  useEffect(() => {
    const notificationSubscription = websocketApi.subscribe(`pushNotification-${userId}`, () => {
      getNotifications(page);
    });
    return notificationSubscription.unsubscribe;
  }, [getNotifications, page, userId]);

  useEffect(() => {
    if (reload) {
      setReload(false);
      getNotifications(page);
      getInvitations();
    }
  }, [getNotifications, getInvitations, page, reload, setReload]);

  useEffect(() => {
    getNotifications(page);
    getInvitations();
  }, [getNotifications, getInvitations, page]);

  const markAsInteracted = useCallback((notificationId) => {
    api.notifications.markAsInteracted(userId, notificationId,
      () => markNotificationAsInteracted(notificationId),
      () => { });
  }, [userId, markNotificationAsInteracted]);

  const markAsSeen = useCallback((newNotifications, markAllAsSeen) => {
    api.notifications.markAsSeen(userId, newNotifications, markAllAsSeen,
      () => markNotificationsAsSeen(newNotifications, markAllAsSeen),
      () => { });
  }, [userId, markNotificationsAsSeen]);

  const markAllAsSeen = useCallback(() => {
    markAsSeen([], true);
  }, [markAsSeen]);

  useEffect(() => {
    if (popupOpen) markAllAsSeen();
  }, [popupOpen, markAllAsSeen]);

  const notificationContent = useMemo(() => (
    <StyledNotificationTray>
      <StyledMenu borderless secondary vertical>
        <div style={{ padding: 5 }}>
          <Header style={{ marginBottom: 0 }}>
            {t('notifications.title')}
            {' '}
            {total > 0 && `(${total} new)`}
          </Header>
          <Divider style={{ marginTop: 3, marginBottom: 0 }} />
        </div>
        {loading ? <Loader active /> : null}
        {!loading && acceptableInvitations.length ? (
          <Menu.Item
            style={{
              borderBottom: '1px solid', borderColor: '#E9EBEE', padding: '.833em 1em', borderRadius: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', textOverflow: 'ellipsis', marginTop: 5, marginBottom: 5, width: '99%', gap: 10
            }}
            fitted
            link
            as={Link}
            to="/invitations"
          >
            <Icon name="paper plane" size="big" style={{ width: 50, margin: 0 }} />
            <div>
              <h4>Invitations</h4>
              <div>There {util.pluralise(acceptableInvitations.length, 'is', 'are', false)} {util.pluralise(acceptableInvitations.length, 'invitation', 'invitations')} waiting for you.</div>
            </div>
          </Menu.Item>
        ) : null}
        {!loading && notifications.length ? (
          <>
            {notifications.map(notification => <NotificationItem notification={notification} user={user} key={notification._id} markNotificationAsInteracted={markAsInteracted} invitations={invitations} onInvitationHandled={onInvitationHandled} />)}
            {canExtend
              ? (
                <Button
                  content="Load more"
                  onClick={() => requestExtendNotifications()}
                  loading={loading}
                  style={{ marginTop: 5, width: '99%' }}
                />
              ) : null
            }
          </>
        ) : null}
        {!loading && !notifications.length ? (
          <Menu.Item>
            <EmptyBox>
              <h5>{t('notifications.empty')}</h5>
            </EmptyBox>
          </Menu.Item>
        ) : null}
      </StyledMenu>
    </StyledNotificationTray>
  ), [total, loading, notifications, markAsInteracted, canExtend, requestExtendNotifications, user, t, invitations, onInvitationHandled, acceptableInvitations]);

  if (isMobile) return notificationContent;
  return (
    <Popup
      on="click"
      style={{ width: 'fit-content' }}
      positionFixed
      wide="very"
      position="bottom right"
      offset={[5, 4]}
      open={popupOpen}
      onClose={() => setPopupOpen(false)}
      trigger={(
        <div style={style}>
          <CircularButtonWithNumber
            onClick={() => setPopupOpen(!popupOpen)}
            icon='bell'
            count={(total === 0 && acceptableInvitations.length !== 0) ? '●' : total}
          />
        </div>
      )}
      content={notificationContent}
    />
  );
}));

export default withRouter(withTranslation()(connect(mapStateToProps, mapDispatchToProps)(NotificationTray)));
